import React, {useState, useEffect, useMemo} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Animated,
  Platform,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {spacing, borderRadius} from '../constants';
import {omaCookieImage, omaBinocularsImage} from './privacyBannerImages';

const STORAGE_KEY = 'oma_privacy_banner_dismissed';

// Check if banner was already dismissed
const wasDismissed = (): boolean => {
  if (Platform.OS !== 'web') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

// Save dismissal to storage
const saveDismissal = () => {
  if (Platform.OS !== 'web') return;
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
};

interface PrivacyBannerProps {
  onOpenPrivacyPolicy?: () => void;
}

type BannerVariant = 'cookie' | 'binoculars';

export default function PrivacyBanner({onOpenPrivacyPolicy}: PrivacyBannerProps) {
  const {t} = useTranslation();
  const [isVisible, setIsVisible] = useState(() => !wasDismissed());
  const fadeAnim = useMemo(() => new Animated.Value(0), []);

  // Randomly pick a variant
  const variant = useMemo<BannerVariant>(
    () => (Math.random() > 0.5 ? 'cookie' : 'binoculars'),
    []
  );

  const bannerContent = {
    cookie: {
      image: omaCookieImage,
      title: t('privacy.cookieTitle'),
      subtitle: t('privacy.cookieSubtitle'),
    },
    binoculars: {
      image: omaBinocularsImage,
      title: t('privacy.binocularsTitle'),
      subtitle: t('privacy.binocularsSubtitle'),
    },
  };

  const content = bannerContent[variant];

  // Fade in on mount
  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);

  // Auto-close after 20 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, 20000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    saveDismissal();
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      setIsVisible(false);
    });
  };

  if (!isVisible) return null;

  return (
    <Animated.View style={[styles.container, {opacity: fadeAnim}]}>
      {/* Grandma image - positioned to overflow */}
      <View style={styles.imageContainer}>
        <Image
          source={content.image}
          style={styles.grandmaImage}
          resizeMode="contain"
        />
      </View>

      {/* Banner content */}
      <View style={styles.banner}>
        <View style={styles.textContainer}>
          <Text style={styles.title}>{content.title}</Text>
          <Text style={styles.subtitle}>{content.subtitle}</Text>
          {onOpenPrivacyPolicy && (
            <TouchableOpacity onPress={onOpenPrivacyPolicy} activeOpacity={0.7}>
              <Text style={styles.privacyLink}>{t('privacy.readMore')}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Close button */}
        <TouchableOpacity
          style={styles.closeButton}
          onPress={handleClose}
          activeOpacity={0.7}>
          <Text style={styles.closeButtonText}>✕</Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
    alignItems: 'flex-start',
    zIndex: 50,
  },
  imageContainer: {
    position: 'absolute',
    bottom: 0,
    left: 5,
    zIndex: 51,
    // Allow image to overflow upward
    overflow: 'visible',
  },
  grandmaImage: {
    width: 140,
    height: 165,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 250, 245, 0.98)',
    borderRadius: borderRadius.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingLeft: 145, // Make room for the overflowing image
    maxWidth: 520,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    borderWidth: 1,
    borderColor: 'rgba(238, 140, 43, 0.2)',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    fontWeight: '700',
    color: '#221910',
    marginBottom: 4,
  },
  subtitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 13,
    fontWeight: '400',
    color: '#666',
    lineHeight: 18,
  },
  privacyLink: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 12,
    fontWeight: '600',
    color: '#ee8c2b',
    marginTop: 6,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.sm,
  },
  closeButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '600',
  },
});
